import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useAiReviewPolicyQuery,
  useAutoApprovedHistoryQuery,
  useSetAiReviewPolicyMutation,
  type AutoApprovedItem,
} from '@/lib/api/ai-review-policy';
import type { RoutingProposal } from '@/lib/api/review-proposals';
import { proposalHeadline, proposalTypeLabel } from '@/routes/engagements/$engagementId/proposals';
import { cn } from '@/lib/utils/cn';
import { formatDateTime } from '@/lib/utils/format';

/**
 * AI review — `/settings/ai-review`.
 *
 * The firm's "auto-approve AI proposals" switch and the history of what the
 * system approved under it. Off (default): every AI proposal waits in the
 * engagement's review queue. On: proposals are approved and committed as
 * they arrive, and each one is listed here and under "Auto-approved" in the
 * engagement's review queue.
 */
export const Route = createFileRoute('/settings/ai-review')({
  component: AiReviewSettings,
});

function AiReviewSettings() {
  const policyQuery = useAiReviewPolicyQuery();
  const historyQuery = useAutoApprovedHistoryQuery();
  const setPolicy = useSetAiReviewPolicyMutation();
  // Turning it ON removes a human check, so it takes a second, explicit click.
  const [confirmingOn, setConfirmingOn] = useState(false);

  if (policyQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading AI review policy…
      </div>
    );
  }
  if (policyQuery.error) return <ErrorMessage error={policyQuery.error} />;
  const policy = policyQuery.data;
  if (!policy) return null;
  const on = policy.auto_approve_proposals;

  function onToggle(next: boolean) {
    if (setPolicy.isPending) return;
    if (next) {
      setConfirmingOn(true);
      return;
    }
    setConfirmingOn(false);
    setPolicy.mutate(false);
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="t-h1 font-semibold text-ink-900">AI review</h1>
        <p className="t-meta text-ink-500">
          Decide whether AI proposals wait for a person or are approved as they arrive.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-4">
            <span>Auto-approve AI proposals</span>
            <span className="flex items-center gap-2.5">
              <span
                className={cn(
                  'text-body-sm font-medium',
                  on ? 'text-posture-healthy' : 'text-ink-500',
                )}
              >
                {on ? 'On' : 'Off'}
              </span>
              <PolicySwitch checked={on} onChange={onToggle} disabled={setPolicy.isPending} />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-body-sm text-ink-700">
          {on ? (
            <p>
              New AI proposals are approved and added to the engagement as soon as they arrive, with
              no one reviewing them first. Every one is recorded in the history below.
            </p>
          ) : (
            <p>
              Every AI proposal waits in the engagement&rsquo;s review queue until a person approves
              or rejects it.
            </p>
          )}
          <ul className="list-disc space-y-1 pl-5 text-ink-500">
            <li>Applies to every engagement, and only to proposals that arrive after it is on.</li>
            <li>
              Proposals the AI was not confident enough about, and ones that repeat something
              already in the baseline, are still rejected rather than approved.
            </li>
            <li>
              Change orders always wait for a person, because they amend the contract itself and can
              remove deliverables.
            </li>
            <li>
              Vendor scoring is not affected: AI readings there are still accepted criterion by
              criterion and the round is submitted by a person.
            </li>
          </ul>
          {policy.updated_at ? (
            <p className="t-meta text-ink-500">
              Last changed {formatDateTime(policy.updated_at)}
              {policy.updated_by_name ? ` by ${policy.updated_by_name}` : ''}.
              {on && policy.updated_by_name
                ? ` Records it approves are attributed to ${policy.updated_by_name}.`
                : ''}
            </p>
          ) : null}

          {confirmingOn && !on ? (
            <div className="rounded-card border border-accent-amber-600 bg-paper-100 p-3">
              <p className="font-medium text-ink-900">Turn on auto-approve?</p>
              <p className="mt-1 text-ink-700">
                From now on, deliverables, checkpoints, deviations and other AI proposals will go
                straight into the engagement record without review. Change orders still wait for a
                person. You can turn it off at any time; what was already approved stays.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={setPolicy.isPending}
                  onClick={() =>
                    setPolicy.mutate(true, { onSuccess: () => setConfirmingOn(false) })
                  }
                >
                  {setPolicy.isPending ? 'Turning on…' : 'Turn on auto-approve'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={setPolicy.isPending}
                  onClick={() => setConfirmingOn(false)}
                >
                  Keep it off
                </Button>
              </div>
            </div>
          ) : null}
          {setPolicy.error ? <ErrorMessage error={setPolicy.error} /> : null}
        </CardContent>
      </Card>

      <section className="space-y-2">
        <div>
          <h2 className="t-h2 font-semibold text-ink-900">Auto-approved history</h2>
          <p className="t-meta text-ink-500">
            The most recent 100 proposals approved without a person, across all engagements.
          </p>
        </div>
        {historyQuery.isLoading ? (
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading history…
          </div>
        ) : historyQuery.error ? (
          <ErrorMessage error={historyQuery.error} />
        ) : (
          <DataGrid
            caption="Auto-approved AI proposals"
            columns={HISTORY_COLUMNS}
            rows={historyQuery.data?.items ?? []}
            rowKey={(row) => row.id}
            emptyState={
              <EmptyState
                title="Nothing has been auto-approved"
                description="When auto-approve is on, each proposal the system approves is listed here."
              />
            }
          />
        )}
      </section>
    </div>
  );
}

const HISTORY_COLUMNS: ReadonlyArray<DataGridColumn<AutoApprovedItem>> = [
  {
    key: 'when',
    header: 'Approved',
    cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.decision_at)}</span>,
  },
  {
    key: 'engagement',
    header: 'Engagement',
    cell: (row) => (
      <Link
        to="/engagements/$engagementId/proposals"
        params={{ engagementId: row.engagement_id }}
        className="text-accent-amber-700 underline-offset-2 hover:underline"
      >
        {row.engagement_name}
      </Link>
    ),
  },
  { key: 'type', header: 'Type', cell: (row) => proposalTypeLabel(row.proposal_type) },
  {
    key: 'what',
    header: 'What was approved',
    // proposalHeadline only reads proposal_type + proposed_value.
    cell: (row) => proposalHeadline(row as unknown as RoutingProposal),
  },
  {
    key: 'confidence',
    header: 'AI confidence',
    cell: (row) => <AiConfidenceChip value={row.confidence} />,
  },
];

/** Same switch shape as the notification-policy editor's channel toggle. */
function PolicySwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="Auto-approve AI proposals"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-4 w-7 flex-none items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
        'disabled:opacity-50',
        checked ? 'bg-posture-healthy' : 'bg-ink-200',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inline-block h-3 w-3 rounded-full bg-paper-0 shadow-sm transition-transform',
          checked ? 'translate-x-3.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
